import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceStore } from '../store/workspace';
import { useAuthStore } from '../store/auth';
import { api } from '../lib/api';
import type { Workspace } from '@responsedock/types';

export function useWorkspace() {
  const { activeWorkspaceId, workspaces, setActiveWorkspace, setWorkspaces } = useWorkspaceStore();
  const isRespondentEmbed = useAuthStore((s) => s.isEmbedded && s.embedContext?.role === 'respondent');
  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) ?? null;
  const qc = useQueryClient();

  const workspaceQuery = useQuery({
    queryKey: ['workspace', activeWorkspaceId],
    queryFn: async () => {
      if (!activeWorkspaceId) return null;
      const res = await api.get<{ workspace: Workspace }>(`/workspaces/${activeWorkspaceId}`);
      return res.data.workspace;
    },
    enabled: !!activeWorkspaceId && !isRespondentEmbed,
  });

  const updateWorkspace = useMutation({
    mutationFn: async (data: Partial<Workspace>) => {
      if (!activeWorkspaceId) throw new Error('No active workspace');
      const res = await api.patch<{ workspace: Workspace }>(`/workspaces/${activeWorkspaceId}`, data);
      return res.data.workspace;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace', activeWorkspaceId] });
    },
  });

  const createWorkspace = useMutation({
    mutationFn: async (name: string) => {
      const res = await api.post<{ workspace: Workspace & { role: string } }>('/workspaces', { name });
      return res.data.workspace;
    },
    onSuccess: (newWorkspace) => {
      const current = useWorkspaceStore.getState().workspaces;
      setWorkspaces([...current, newWorkspace]);
      setActiveWorkspace(newWorkspace.id);
    },
  });

  return {
    workspaceId: activeWorkspaceId,
    workspace: workspaceQuery.data ?? activeWorkspace,
    isLoading: workspaceQuery.isLoading,
    workspaces,
    setActiveWorkspace,
    updateWorkspace,
    createWorkspace,
  };
}
